import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Mail, Phone, Building2, Lock, Save, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PrivacyPanel from "../components/PrivacyPanel";

export default function Account() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();

    const [nom, setNom] = useState(user?.nom || "");
    const [prenom, setPrenom] = useState(user?.prenom || "");
    const [phone, setPhone] = useState(user?.phone || "");
    const [nomEtablissement, setNomEtablissement] = useState(user?.nom_etablissement || "");
    const [niveau, setNiveau] = useState(user?.niveau || "");
    const [email, setEmail] = useState(user?.email || "");

    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [newPassword2, setNewPassword2] = useState("");

    const [savingProfile, setSavingProfile] = useState(false);
    const [savingSecurity, setSavingSecurity] = useState(false);

    if (!user) {
        return (
            <div className="min-h-screen bg-paper flex items-center justify-center">
                <Loader2 className="animate-spin text-orange" />
            </div>
        );
    }

    const isResponsable = user.role === "responsable";
    const isAlternant = user.role === "alternant";

    const saveProfile = async (e) => {
        e.preventDefault();
        setSavingProfile(true);
        try {
            const payload = { nom, prenom, phone };
            if (isResponsable) payload.nom_etablissement = nomEtablissement;
            if (isAlternant) payload.niveau = niveau;
            const { data } = await api.patch("/account/profile", payload);
            if (data.updated?.length) {
                toast.success("Profil mis à jour.");
                if (refresh) await refresh();
            } else {
                toast("Aucune modification détectée.");
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur lors de la sauvegarde.");
        } finally {
            setSavingProfile(false);
        }
    };

    const saveSecurity = async (e) => {
        e.preventDefault();
        if (!currentPassword) {
            toast.error("Mot de passe actuel requis pour modifier l'e-mail ou le mot de passe.");
            return;
        }
        if (newPassword && newPassword !== newPassword2) {
            toast.error("Les nouveaux mots de passe ne correspondent pas.");
            return;
        }
        if (newPassword && newPassword.length < 6) {
            toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères.");
            return;
        }
        setSavingSecurity(true);
        try {
            const payload = { current_password: currentPassword };
            if (email && email.toLowerCase() !== user.email) payload.email = email;
            if (newPassword) payload.new_password = newPassword;
            const { data } = await api.patch("/account/profile", payload);
            if (data.updated?.length) {
                toast.success("Identifiants mis à jour. Un e-mail de confirmation vous a été envoyé.");
                setCurrentPassword(""); setNewPassword(""); setNewPassword2("");
                if (refresh) await refresh();
            } else {
                toast("Aucune modification détectée.");
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur lors de la sauvegarde.");
        } finally {
            setSavingSecurity(false);
        }
    };

    return (
        <div className="min-h-screen bg-paper flex flex-col">
            <Navbar />
            <main className="flex-1 max-w-3xl mx-auto px-6 lg:px-10 py-12 w-full" data-testid="account-page">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="text-xs font-mono text-muted2 hover:text-ink mb-3 inline-flex items-center gap-1"
                >
                    <ArrowLeft size={12} /> Retour
                </button>

                <div className="label-eyebrow">Mon compte</div>
                <h1 className="h-display text-4xl sm:text-5xl font-semibold leading-tight mt-1.5">Profil & sécurité</h1>
                <p className="text-sm text-muted2 mt-2">Gérez vos informations personnelles et vos identifiants de connexion.</p>

                {/* Profile section */}
                <form onSubmit={saveProfile} className="panel p-6 mt-8 space-y-4" data-testid="account-profile-form">
                    <div className="label-eyebrow flex items-center gap-2"><User size={14} className="text-orange" /> Informations personnelles</div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <label className="label-eyebrow mb-2 block">Prénom</label>
                            <input className="field-input" value={prenom} onChange={(e) => setPrenom(e.target.value)} data-testid="account-prenom-input" />
                        </div>
                        <div>
                            <label className="label-eyebrow mb-2 block">Nom</label>
                            <input className="field-input" value={nom} onChange={(e) => setNom(e.target.value)} data-testid="account-nom-input" />
                        </div>
                    </div>
                    <div>
                        <label className="label-eyebrow mb-2 block flex items-center gap-1.5"><Phone size={11} /> Téléphone</label>
                        <input className="field-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="06 12 34 56 78" data-testid="account-phone-input" />
                    </div>
                    {isResponsable && (
                        <div>
                            <label className="label-eyebrow mb-2 block flex items-center gap-1.5"><Building2 size={11} /> Nom de l'établissement</label>
                            <input className="field-input" value={nomEtablissement} onChange={(e) => setNomEtablissement(e.target.value)} data-testid="account-etablissement-input" />
                        </div>
                    )}
                    {isAlternant && (
                        <div>
                            <label className="label-eyebrow mb-2 block">Année BTS</label>
                            <div className="grid grid-cols-3 gap-2" data-testid="account-niveau-selector">
                                <button
                                    type="button"
                                    onClick={() => setNiveau("1ere")}
                                    className={`p-2.5 text-xs border transition-colors ${niveau === "1ere" ? "bg-ink text-white border-ink" : "border-rule hover:border-ink/40"}`}
                                    data-testid="account-niveau-1ere"
                                >
                                    1ère année BTS
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setNiveau("2eme")}
                                    className={`p-2.5 text-xs border transition-colors ${niveau === "2eme" ? "bg-ink text-white border-ink" : "border-rule hover:border-ink/40"}`}
                                    data-testid="account-niveau-2eme"
                                >
                                    2ème année BTS
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setNiveau("")}
                                    className={`p-2.5 text-xs border transition-colors ${niveau === "" ? "bg-ink text-white border-ink" : "border-rule hover:border-ink/40"}`}
                                    data-testid="account-niveau-none"
                                >
                                    Non précisé
                                </button>
                            </div>
                        </div>
                    )}
                    <button type="submit" disabled={savingProfile} className="btn-primary !text-sm" data-testid="account-save-profile-button">
                        {savingProfile ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        {savingProfile ? "Enregistrement…" : "Enregistrer"}
                    </button>
                </form>

                {/* Read-only role + invite_code panel */}
                <div className="panel p-5 mt-4 bg-surface" data-testid="account-readonly-panel">
                    <div className="label-eyebrow mb-2">Informations de compte (non modifiables)</div>
                    <dl className="text-sm grid sm:grid-cols-2 gap-3">
                        <div>
                            <dt className="text-xs text-muted2">Rôle</dt>
                            <dd className="font-mono">{user.role}</dd>
                        </div>
                        {user.invite_code && (
                            <div>
                                <dt className="text-xs text-muted2">Code d'établissement</dt>
                                <dd className="font-mono text-orange font-bold">{user.invite_code}</dd>
                            </div>
                        )}
                        {user.tutor_code && (
                            <div>
                                <dt className="text-xs text-muted2">Code tuteur</dt>
                                <dd className="font-mono text-orange font-bold">{user.tutor_code}</dd>
                            </div>
                        )}
                    </dl>
                </div>

                {/* Security section */}
                <form onSubmit={saveSecurity} className="panel p-6 mt-8 space-y-4 border-l-4 border-l-orange" data-testid="account-security-form">
                    <div className="label-eyebrow flex items-center gap-2"><Lock size={14} className="text-orange" /> Identifiants & sécurité</div>
                    <p className="text-xs text-muted2">
                        Toute modification de l'e-mail ou du mot de passe nécessite votre <strong>mot de passe actuel</strong> et déclenche un e-mail de confirmation.
                    </p>
                    <div>
                        <label className="label-eyebrow mb-2 block flex items-center gap-1.5"><Mail size={11} /> Adresse e-mail</label>
                        <input type="email" className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="account-email-input" />
                    </div>
                    <div className="border-t border-rule pt-4">
                        <label className="label-eyebrow mb-2 block">Mot de passe actuel *</label>
                        <input type="password" className="field-input" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} autoComplete="current-password" data-testid="account-current-password-input" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <label className="label-eyebrow mb-2 block">Nouveau mot de passe</label>
                            <input type="password" className="field-input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" data-testid="account-new-password-input" />
                            <p className="text-[11px] text-muted2 mt-1">Laissez vide pour ne pas changer.</p>
                        </div>
                        <div>
                            <label className="label-eyebrow mb-2 block">Confirmer</label>
                            <input type="password" className="field-input" value={newPassword2} onChange={(e) => setNewPassword2(e.target.value)} autoComplete="new-password" data-testid="account-new-password2-input" />
                        </div>
                    </div>
                    <button type="submit" disabled={savingSecurity} className="btn-primary !text-sm" data-testid="account-save-security-button">
                        {savingSecurity ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                        {savingSecurity ? "Enregistrement…" : "Mettre à jour mes identifiants"}
                    </button>
                </form>

                <PrivacyPanel />
            </main>
            <Footer />
        </div>
    );
}
